import React, { useState, useEffect, useRef } from 'react';
import { Select, Input, Button, Table, message, Spin, Card, Space, Popconfirm, Tag, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { Product, Entitlement, ProductSKUConfig, ProductSKUConfigEntitlement, ProductAddonConfig } from '../../types';
import { EntityType } from './types';
import { useFieldConfig } from '../../contexts/FieldConfigContext';
import { formatEntitlementLabel, sanitizeTextInput } from '../../utils/utils';

const { Option } = Select;

const makeEntitlementTagRender = (entitlementsList: Array<{ id?: number; name: string; value: string; description?: string; needs_quantity: boolean; unit?: string }>) =>
  (props: { label: React.ReactNode; value: unknown; closable: boolean; onClose: () => void }) => {
    const { value, closable, onClose } = props;
    const ent = entitlementsList.find((e) => e.id === value);
    const labelStr = ent ? formatEntitlementLabel(ent) : String(value ?? '');
    return (
      <Tooltip title={labelStr} placement="top">
        <Tag
          closable={closable}
          onClose={onClose}
          style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', margin: '2px 4px 2px 0' }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelStr}</span>
        </Tag>
      </Tooltip>
    );
  };

const ALLOWED_REGION_OPTIONS = ['NAM', 'APJC', 'US', 'CA', 'AU', 'IN', 'JP', 'UAE', 'EU'];

interface ProductConfigRow {
  key: string;
  product_id: number;
  product_name: string;
  operation: string;
  operand: number | null;
  force_region_name: string;
  allowed_regions: string; // comma-separated region codes (addon SKUs only)
  entitlement_ids: number[];
  isNew: boolean; // Track if this is a new addition
  isModified: boolean; // Track if existing config was modified
}

interface SKUConfigEditorProps {
  selectedSKU: {
    id: number;
    name: string;
  };
  entityType?: string;
  onConfigChange?: (changes: {
    skuId: number;
    entityType?: string;
    deleteOperations: Array<{
      ProductID: number;
      SkuID: number;
      entitlementIds: number[];
    }>;
    addOperations: Array<{
      config: {
        ProductID: number;
        SkuID: number;
        Operation?: string;
        Operand?: number;
        ForceRegionName?: string;
        AllowedRegions?: string;
      };
      entitlements: Array<{
        ProductSkuConfigProductID: number;
        ProductSkuConfigSkuID: number;
        EntitlementID: number;
      }>;
    }>;
  }) => void;
}

const SKUConfigEditor: React.FC<SKUConfigEditorProps> = ({ selectedSKU, entityType, onConfigChange }) => {
  const { isFieldDisabled } = useFieldConfig();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [_originalConfigs, setOriginalConfigs] = useState<ProductConfigRow[]>([]);
  const [currentConfigs, setCurrentConfigs] = useState<ProductConfigRow[]>([]);
  const [removedConfigs, setRemovedConfigs] = useState<ProductConfigRow[]>([]);
  const loadingRef = useRef(false);
  const lastLoadedSkuId = useRef<number | null>(null);

  useEffect(() => {
    if (selectedSKU?.id && selectedSKU.id !== lastLoadedSkuId.current && !loadingRef.current) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSKU.id]); // Only reload when SKU ID changes, not when object reference changes

  const loadData = async () => {
    if (loadingRef.current) {
      return; // Prevent duplicate calls
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      // Fetch all data using apiService
      const [productsData, entitlementsData, allConfigs, allConfigEntitlements, allAddonConfigs] = await Promise.all([
        apiService.getProducts(),
        apiService.getEntitlements(),
        apiService.getProductSkuConfigs(),
        apiService.getProductSkuConfigEntitlements(),
        apiService.getProductAddonConfigs(),
      ]);

      setProducts(productsData || []);
      setEntitlements(entitlementsData || []);

      // Filter configs for this SKU
      const skuConfigs = (allConfigs || []).filter(
        (config: ProductSKUConfig) => config.sku_id === selectedSKU.id
      );

      // Find addon config for this SKU (contains allowed_regions)
      const addonConfig = (allAddonConfigs || []).find(
        (ac: ProductAddonConfig) => ac.sku_id === selectedSKU.id
      );

      // Filter entitlements for this SKU
      const skuConfigEntitlements = (allConfigEntitlements || []).filter(
        (ent: ProductSKUConfigEntitlement) =>
          ent.product_sku_config_sku_id === selectedSKU.id
      );

      // Build ProductConfigRow objects
      const configRows: ProductConfigRow[] = skuConfigs.map((config: ProductSKUConfig) => {
        const product = productsData.find((p: Product) => p.id === config.product_id);
        const entIds = skuConfigEntitlements
          .filter((e: ProductSKUConfigEntitlement) => e.product_sku_config_product_id === config.product_id)
          .map((e: ProductSKUConfigEntitlement) => e.entitlement_id);

        return {
          key: `${config.product_id}`,
          product_id: config.product_id,
          product_name: product?.name || 'Unknown Product',
          operation: config.operation || '',
          operand: config.operand || null,
          force_region_name: config.force_region_name || '',
          // For addon SKUs, allowed_regions comes from product_addon_config
          allowed_regions: addonConfig?.allowed_regions || config.allowed_regions || '',
          entitlement_ids: entIds,
          isNew: false,
          isModified: false,
        };
      });

      setOriginalConfigs(configRows);
      setCurrentConfigs([...configRows]);
      setRemovedConfigs([]);
      lastLoadedSkuId.current = selectedSKU.id;
    } catch (error) {
      message.error('Failed to load SKU configuration data');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleAddProduct = () => {
    const newKey = `new-${Date.now()}`;
    const newConfig: ProductConfigRow = {
      key: newKey,
      product_id: 0,
      product_name: '',
      operation: '',
      operand: null,
      force_region_name: '',
      allowed_regions: '',
      entitlement_ids: [],
      isNew: true,
      isModified: false,
    };
    setCurrentConfigs([...currentConfigs, newConfig]);
  };

  const handleRemoveProduct = (key: string) => {
    const config = currentConfigs.find((c) => c.key === key);
    if (!config) {
      return;
    }

    // If it's not a new config, track it as removed for DELETE
    if (!config.isNew) {
      setRemovedConfigs([...removedConfigs, config]);
    }

    // Remove from current configs
    setCurrentConfigs(currentConfigs.filter((c) => c.key !== key));
  };

  const handleFieldChange = (key: string, field: string, value: unknown) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeTextInput(value) : value;
    setCurrentConfigs(
      currentConfigs.map((config) => {
        if (config.key === key) {
          const updated = { ...config, [field]: sanitizedValue };

          // If product changed, clear entitlements
          if (field === 'product_id') {
            const product = products.find((p) => p.id === value);
            updated.product_name = product?.name || '';
            updated.entitlement_ids = [];
          }

          // Mark as modified if it's not a new config
          if (!config.isNew) {
            updated.isModified = true;
          }

          return updated;
        }
        return config;
      })
    );
  };

  const handleRemoveAllConfigs = () => {
    // Mark all existing configs as removed
    const existing = currentConfigs.filter((c) => !c.isNew);
    setRemovedConfigs([...removedConfigs, ...existing]);
    setCurrentConfigs([]);
    message.info('All configurations marked for removal. Click Save to apply changes.');
  };

  const handleSave = () => {
    // Validate: all current configs must have product_id selected
    const invalidConfigs = currentConfigs.filter((c) => !c.product_id || c.product_id === 0);
    if (invalidConfigs.length > 0) {
      message.error('Please select a product for all configurations');
      return;
    }

    setSaving(true);
    try {
      // Calculate configurations to delete (removed + modified)
      const configsToDelete = [...removedConfigs];
      const modifiedConfigs = currentConfigs.filter((c) => c.isModified && !c.isNew);
      configsToDelete.push(...modifiedConfigs);

      // Build delete operations with ProductID, SkuID, and entitlements
      const deleteOperations = configsToDelete.map((config) => ({
        ProductID: config.product_id,
        SkuID: selectedSKU.id,
        entitlementIds: config.entitlement_ids || [],
      }));

      // Calculate configurations to add (new + modified)
      const configsToAdd = currentConfigs.filter((c) => c.isNew || c.isModified);

      // Build add operations with config data and entitlements
      const addOperations = configsToAdd.map((config) => {
        const configData: {
          ProductID: number;
          SkuID: number;
          Operation?: string;
          Operand?: number;
          ForceRegionName?: string;
          AllowedRegions?: string;
        } = {
          ProductID: config.product_id,
          SkuID: selectedSKU.id,
        };

        if (config.operation) {
          configData.Operation = config.operation;
        }
        if (config.operand !== null && config.operand !== undefined) {
          configData.Operand = config.operand;
        }
        if (config.force_region_name) {
          configData.ForceRegionName = config.force_region_name;
        }
        if (entityType === 'sku_addon' && config.allowed_regions) {
          configData.AllowedRegions = config.allowed_regions;
        }

        const entitlements = config.entitlement_ids.map((entitlementId) => ({
          ProductSkuConfigProductID: config.product_id,
          ProductSkuConfigSkuID: selectedSKU.id,
          EntitlementID: entitlementId,
        }));

        return {
          config: configData,
          entitlements,
        };
      });

      // Notify parent component of changes
      if (onConfigChange) {
        onConfigChange({
          skuId: selectedSKU.id,
          entityType,
          deleteOperations,
          addOperations,
        });
      }

      // Mark all current configs as saved (no longer new or modified)
      setCurrentConfigs(
        currentConfigs.map((config) => ({
          ...config,
          isNew: false,
          isModified: false,
        }))
      );
      setOriginalConfigs([...currentConfigs]);
      setRemovedConfigs([]);

      message.success('SKU configuration changes saved. Review and submit to apply.');
    } catch (error) {
      message.error('Failed to save configuration changes');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: entityType === 'sku_addon' ? 'Base Product' : 'Product',
      dataIndex: 'product_id',
      key: 'product',
      width: 200,
      render: (productId: number, record: ProductConfigRow) => {
        const fieldName = entityType === 'sku_addon' ? 'base_product' : 'product';
        const entityTypeEnum = entityType === 'sku_addon' ? EntityType.SKU_ADDON :
          entityType === 'sku_suite' ? EntityType.SKU_SUITE :
            EntityType.SKU_STANDALONE;
        const isExisting = !record.isNew;
        const isDisabled = isExisting && isFieldDisabled(entityTypeEnum, fieldName);

        return (
          <Select
            value={productId || undefined}
            placeholder="Select product"
            style={{ width: '100%' }}
            showSearch
            disabled={isDisabled}
            filterOption={(input, option) =>
              (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => handleFieldChange(record.key, 'product_id', value)}
          >
            {products.map((product) => (
              <Option key={product.id} value={product.id}>
                {product.name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Operation',
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
      render: (operation: string, record: ProductConfigRow) => (
        <Select
          value={operation || undefined}
          placeholder="Operation"
          style={{ width: '100%' }}
          allowClear
          onChange={(value) => handleFieldChange(record.key, 'operation', value || '')}
        >
          <Option value="MULTIPLY">MULTIPLY</Option>
          <Option value="OP_REPLACE">OP_REPLACE</Option>
        </Select>
      ),
    },
    {
      title: 'Operand',
      dataIndex: 'operand',
      key: 'operand',
      width: 100,
      render: (operand: number | null, record: ProductConfigRow) => (
        <Input
          type="number"
          value={operand ?? ''}
          placeholder="Value"
          onChange={(e) =>
            handleFieldChange(record.key, 'operand', e.target.value ? parseInt(e.target.value) : null)
          }
        />
      ),
    },
    {
      title: 'Force Region',
      dataIndex: 'force_region_name',
      key: 'force_region_name',
      width: 120,
      render: (forceRegion: string, record: ProductConfigRow) => (
        <Input
          value={forceRegion}
          placeholder="Region"
          onChange={(e) => handleFieldChange(record.key, 'force_region_name', e.target.value)}
          onBlur={(e) => handleFieldChange(record.key, 'force_region_name', e.target.value.trim())}
        />
      ),
    },
    ...(entityType === 'sku_addon' ? [{
      title: 'Allowed Regions',
      dataIndex: 'allowed_regions',
      key: 'allowed_regions',
      width: 260,
      render: (allowedRegions: string, record: ProductConfigRow) => {
        // Convert stored comma-separated string → string[] for the Select
        const selected = allowedRegions
          ? allowedRegions.split(',').map((r) => r.trim()).filter(Boolean)
          : [];

        return (
          <Select
            mode="multiple"
            value={selected}
            placeholder="Select regions"
            style={{ width: '100%' }}
            onChange={(values: string[]) => {
              // Convert string[] → comma-separated string for storage
              handleFieldChange(record.key, 'allowed_regions', values.join(','));
            }}
          >
            {ALLOWED_REGION_OPTIONS.map((region) => (
              <Option key={region} value={region}>{region}</Option>
            ))}
          </Select>
        );
      },
    }] : []),
    {
      title: 'Entitlements',
      dataIndex: 'entitlement_ids',
      key: 'entitlements',
      width: 250,
      render: (entitlementIds: number[], record: ProductConfigRow) => {
        const filteredEntitlements = entitlements.filter((e) => e.product_id === record.product_id);
        const entityTypeEnum = entityType === 'sku_addon' ? EntityType.SKU_ADDON :
          entityType === 'sku_suite' ? EntityType.SKU_SUITE :
            EntityType.SKU_STANDALONE;

        // Determine if entitlement field should be disabled
        let isDisabled = false;

        // Always disabled if no product is selected
        if (!record.product_id || record.product_id === 0) {
          isDisabled = true;
        }
        // For existing records (not new), check config
        else if (!record.isNew) {
          isDisabled = isFieldDisabled(entityTypeEnum, 'entitlement_update');
        }
        // For new records, entitlements are always enabled

        return (
          <Select
            mode="multiple"
            value={entitlementIds}
            placeholder="Select entitlements"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ maxWidth: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}
            tagRender={makeEntitlementTagRender(filteredEntitlements)}
            disabled={isDisabled}
            onChange={(value) => handleFieldChange(record.key, 'entitlement_ids', value)}
          >
            {filteredEntitlements.map((ent) => (
              <Option key={ent.id} value={ent.id}>
                {formatEntitlementLabel(ent)}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 80,
      render: (_: unknown, record: ProductConfigRow) => (
        <>
          {record.isNew && <Tag color="green">New</Tag>}
          {record.isModified && <Tag color="orange">Modified</Tag>}
          {!record.isNew && !record.isModified && <Tag color="blue">Existing</Tag>}
        </>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_: unknown, record: ProductConfigRow) => {
        const entityTypeEnum = entityType === 'sku_addon' ? EntityType.SKU_ADDON :
          entityType === 'sku_suite' ? EntityType.SKU_SUITE :
            EntityType.SKU_STANDALONE;
        const isNewRow = record.isNew;
        const isDisabled = !isNewRow && isFieldDisabled(entityTypeEnum, 'delete_config');

        return (
          <Popconfirm
            title="Remove this product configuration?"
            onConfirm={() => handleRemoveProduct(record.key)}
            okText="Yes"
            cancelText="No"
            disabled={isDisabled}
          >
            <Button type="link" danger icon={<DeleteOutlined />} disabled={isDisabled} />
          </Popconfirm>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Card>
        <Spin tip="Loading SKU configuration..." />
      </Card>
    );
  }

  return (
    <Card title={`SKU Configuration: ${selectedSKU.name}`}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <p>
            Configure which products are associated with this SKU. You can add new products, modify existing
            configurations, or remove products entirely.
          </p>
          {removedConfigs.length > 0 && (
            <Tag color="red">
              {removedConfigs.length} configuration(s) marked for removal
            </Tag>
          )}
          {currentConfigs.filter((c) => c.isNew).length > 0 && (
            <Tag color="green">
              {currentConfigs.filter((c) => c.isNew).length} new configuration(s) to add
            </Tag>
          )}
          {currentConfigs.filter((c) => c.isModified).length > 0 && (
            <Tag color="orange">
              {currentConfigs.filter((c) => c.isModified).length} configuration(s) modified
            </Tag>
          )}
        </div>

        <Table
          dataSource={currentConfigs}
          columns={columns}
          pagination={false}
          bordered
          size="small"
          locale={{ emptyText: 'No product configurations. Click "Add Product" to start.' }}
        />

        <Space>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddProduct}
            disabled={isFieldDisabled(
              entityType === 'sku_addon' ? EntityType.SKU_ADDON :
                entityType === 'sku_suite' ? EntityType.SKU_SUITE :
                  EntityType.SKU_STANDALONE,
              'add_product'
            )}
          >
            Add Product
          </Button>
          {currentConfigs.length > 0 && (
            <Popconfirm
              title="Remove all configurations for this SKU?"
              description="This will delete all product associations for this SKU."
              onConfirm={handleRemoveAllConfigs}
              okText="Yes"
              cancelText="No"
              disabled={isFieldDisabled(
                entityType === 'sku_addon' ? EntityType.SKU_ADDON :
                  entityType === 'sku_suite' ? EntityType.SKU_SUITE :
                    EntityType.SKU_STANDALONE,
                'delete_all_config'
              )}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={isFieldDisabled(
                  entityType === 'sku_addon' ? EntityType.SKU_ADDON :
                    entityType === 'sku_suite' ? EntityType.SKU_SUITE :
                      EntityType.SKU_STANDALONE,
                  'delete_all_config'
                )}
              >
                Remove All Configurations
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={removedConfigs.length === 0 && currentConfigs.filter((c) => c.isNew || c.isModified).length === 0}
          >
            Save Changes
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

export default SKUConfigEditor;
